import { MenuItem, Select } from "@mui/material"
import "./listPagination.scss"

export const PAGE_SIZE_OPTIONS = Object.freeze([10, 20, 50])
export const DEFAULT_PAGE_SIZE = 20

// Total pages for a list (always at least 1, so an empty list still reads "page 1 of 1").
export const pageCount = (total, pageSize) => Math.max(1, Math.ceil((Number(total) || 0) / pageSize))

// A stored rows-per-page value, or the default when it isn't one of the offered sizes.
export const validPageSize = (value, options = PAGE_SIZE_OPTIONS) => (options.includes(Number(value)) ? Number(value) : DEFAULT_PAGE_SIZE)

// Window of up to 7 numbered pages centered on the current one.
export const pageWindow = (page, totalPages) => {
  const start = Math.max(1, Math.min(page - 3, totalPages - 6))
  const nums = []
  for (let n = start; n <= Math.min(totalPages, start + 6); n++) nums.push(n)
  return nums
}

// Shared list footer (Exercises, People): "Showing 1–20 of N" + Rows per page on the left,
// Previous / numbered pages / Next on the right. Stateless — the list owns page and size.
// `page` is the page whose rows are ON SCREEN; `loadingPage` (optional) is a page being fetched
// while those rows stay up: the footer dims and says which page is coming, but never claims rows
// that aren't shown. loadingPage may equal `page` — a search/filter/sort change refetches page 1
// while the old query's page 1 stays up — and that is still loading.
// The footer is never aria-busy: it holds the aria-live note, and some screen readers hold back
// announcements from a busy region. Mark the table (the thing being replaced) busy instead.
const ListPagination = ({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  label = "Pagination",
  loadingPage = null,
}) => {
  const totalPages = pageCount(total, pageSize)
  const current = Math.min(Math.max(1, page), totalPages)
  const from = total > 0 ? (current - 1) * pageSize + 1 : 0
  const to = Math.min(current * pageSize, total)
  const loading = loadingPage != null
  return (
    <div className={`list-pagination${loading ? " is-loading" : ""}`}>
      <span className="range">
        <span aria-live="polite">
          Showing {from}&ndash;{to} of {total}
          {loading ? <span className="loading-note">{` · Loading page ${loadingPage}…`}</span> : null}
        </span>
        <span className="rows-per-page">
          Rows per page
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            inputProps={{ "aria-label": "Rows per page" }}
            sx={{ ml: 1, fontSize: 12.5, height: 30, bgcolor: "var(--ct-surface)" }}
          >
            {pageSizeOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </span>
      </span>
      <nav className="controls" aria-label={label}>
        <button type="button" className="pager-text" disabled={current <= 1} onClick={() => onPageChange(current - 1)}>
          <i className="fa-solid fa-chevron-left" aria-hidden="true"></i> Previous
        </button>
        {pageWindow(current, totalPages).map((n) => (
          <button
            type="button"
            key={n}
            className={`pager-num ${n === current ? "is-current" : ""}`}
            onClick={() => onPageChange(n)}
            aria-current={n === current ? "page" : undefined}
          >
            {n}
          </button>
        ))}
        <button type="button" className="pager-text" disabled={current >= totalPages} onClick={() => onPageChange(current + 1)}>
          Next <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>
      </nav>
    </div>
  )
}

export default ListPagination
